/**
 * Fetching /api/rtc/config with retries. Voice connects only after the config
 * has answered (there is no other media path to fall back to), so a transient
 * failure — a 5xx during a deploy, a network blip — is retried with a growing
 * delay before the call says it cannot connect.
 */

/** Waits before each retry: ~30 s in all, then voice reports the failure. */
export const RTC_CONFIG_RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 15_000];

/** While someone stays in a channel after a round gave up, a new round starts this often. */
export const RTC_CONFIG_REASK_MS = 60_000;

export interface RtcConfigRetryOptions<T> {
  load: () => Promise<T>;
  /** Stop retrying (the component unmounted). The promise then resolves null. */
  isCancelled: () => boolean;
  delaysMs?: readonly number[];
  wait?: (ms: number) => Promise<void>;
}

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/**
 * Resolves with the config, or null if cancelled; rejects with the last error
 * once every retry has failed.
 */
export async function loadRtcConfigWithRetry<T>({
  load,
  isCancelled,
  delaysMs = RTC_CONFIG_RETRY_DELAYS_MS,
  wait = sleep,
}: RtcConfigRetryOptions<T>): Promise<T | null> {
  for (let attempt = 0; ; attempt += 1) {
    if (isCancelled()) return null;
    try {
      return await load();
    } catch (err) {
      const delay = delaysMs[attempt];
      if (delay === undefined) throw err;
      await wait(delay);
    }
  }
}
